import React, { useState, useEffect } from 'react';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { StatCard } from '../../../../components/shared/StatCard/StatCard';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DataTable } from '../../../../components/shared/DataTable/DataTable';
import { ActivityEntry, CriticalBook, DashboardSummary, SalesFlowPoint, getCriticalInventory, getDashboardSummary, getRecentActivity, getSalesFlow } from '../../../../services/DashboardService';
import { useToast } from '../../../../hooks/useToast';
import './DashboardView.css';

export const DashboardView = () => {
  const { notify } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [salesFlow, setSalesFlow] = useState<SalesFlowPoint[]>([]);
  const [flowLoading, setFlowLoading] = useState(true);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const ACTIVITY_PREVIEW = 5;
  const [criticalBooks, setCriticalBooks] = useState<CriticalBook[]>([]);
  const [criticalTotal, setCriticalTotal] = useState(0);
  const [criticalLoading, setCriticalLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getDashboardSummary()
      .then(data => { if (!cancelled) setSummary(data); })
      .catch(err => {
        console.error('Error loading dashboard summary:', err);
        if (!cancelled) notify('Panel', 'No se pudieron cargar las cifras del panel.', 'warning');
      })
      .then(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Flujo de ventas por hora: se refresca cada minuto
  useEffect(() => {
    let cancelled = false;
    let warned = false;
    const load = () => {
      getSalesFlow()
        .then(data => { if (!cancelled) setSalesFlow(data); })
        .catch(err => {
          console.error('Error loading sales flow:', err);
          if (!cancelled && !warned) {
            warned = true;
            notify('Panel', 'No se pudo cargar el flujo de ventas.', 'warning');
          }
        })
        .then(() => { if (!cancelled) setFlowLoading(false); });
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Actividad reciente: se refresca cada minuto
  useEffect(() => {
    let cancelled = false;
    let warned = false;
    const load = () => {
      getRecentActivity()
        .then(data => { if (!cancelled) setActivity(data); })
        .catch(err => {
          console.error('Error loading recent activity:', err);
          if (!cancelled && !warned) {
            warned = true;
            notify('Panel', 'No se pudo cargar la actividad reciente.', 'warning');
          }
        })
        .then(() => { if (!cancelled) setActivityLoading(false); });
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Inventario crítico: libros con 5 o menos unidades, los más bajos primero
  useEffect(() => {
    let cancelled = false;
    getCriticalInventory({ maxStock: 5, limit: 5 })
      .then(data => {
        if (cancelled) return;
        setCriticalBooks(data.books);
        setCriticalTotal(data.total);
      })
      .catch(err => {
        console.error('Error loading critical inventory:', err);
        if (!cancelled) notify('Panel', 'No se pudo cargar el inventario crítico.', 'warning');
      })
      .then(() => { if (!cancelled) setCriticalLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const euros = (v: number) => v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
  const placeholder = summaryLoading ? '...' : '—';
  // Tendencia frente a las 24 h anteriores; sin base de comparación no se muestra
  const salesTrend = summary && summary.salesPrev24h > 0
    ? `${summary.sales24h >= summary.salesPrev24h ? '+' : ''}${(((summary.sales24h - summary.salesPrev24h) / summary.salesPrev24h) * 100).toFixed(1)}%`
    : undefined;
  return (
    <div className="dashboard-view">
      <ModuleHeader title="System_Dashboard" subtitle="Resumen ejecutivo del flujo de negocio editorial y logístico." />

      <div className="stat-cards-grid">
        <StatCard label="Ventas Totales (24h)" value={summary ? euros(summary.sales24h) : placeholder} trend={salesTrend} />
        <StatCard label="Libros en Stock" value={summary ? summary.booksInStock.toLocaleString('es-ES') : placeholder} />
        <StatCard label="Pedidos Pendientes" value={summary ? summary.pendingOrders : placeholder} trend={summary && summary.pendingOrdersNew24h > 0 ? `+${summary.pendingOrdersNew24h}` : undefined} />
        <StatCard label="Alertas Sistema" value={0} />
      </div>

      <div className="middle-grid">
        {/* Sales Chart */}
        <div className="sales-chart-container">
          <div className="sales-chart-header">
            <h3 className="section-title">Flujo_Ventas_Tiempo_Real</h3>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color legend-color-green" />
                <span className="legend-text">Ventas</span>
              </div>
            </div>
          </div>
          <div className="chart-wrapper">
            {!flowLoading && salesFlow.length === 0 && (
              <span className="legend-text">Sin datos de ventas disponibles.</span>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesFlow}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-brand-green)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-brand-green)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="var(--color-brand-text)" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--color-brand-text)" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: '#1a1a1a', border: '1px solid #334155', color: 'var(--color-brand-green)' }}
                  formatter={(value: number) => [euros(Number(value)), 'Ventas']}
                />
                <Area type="monotone" dataKey="sales" stroke="var(--color-brand-green)" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-activity-container">
          <h3 className="section-title section-title-mb">Log_Actividad_Reciente</h3>
          <div className="activity-list">
            {!activityLoading && activity.length === 0 && (
              <span className="activity-details">Sin actividad reciente.</span>
            )}
            {(showAllActivity ? activity : activity.slice(0, ACTIVITY_PREVIEW)).map((entry, idx) => (
              <div key={`${entry.kind}-${entry.reference}-${idx}`} className="activity-item">
                <div className="activity-content">
                  <span className="activity-customer">{entry.party}</span>
                  <span className="activity-details">
                    {entry.kind === 'purchase_order'
                      ? `PEDIDO_COMPRA: ${entry.reference}`
                      : entry.invoiceNumber
                        ? `${entry.isReturn ? 'ABONO' : 'FACTURA'}: ${entry.invoiceNumber} (ticket ${entry.reference})`
                        : `${entry.isReturn ? 'DEVOLUCIÓN' : 'TICKET'}: ${entry.reference}`} | {entry.items} uds
                  </span>
                  <span className="activity-status">{entry.status}</span>
                </div>
                <span className="activity-total">{euros(entry.total)}</span>
              </div>
            ))}
          </div>
          {activity.length > ACTIVITY_PREVIEW && (
            <button className="btn-view-all" onClick={() => setShowAllActivity(v => !v)}>
              {showAllActivity ? 'Ver_Menos_Registros' : 'Ver_Todos_Los_Registros'}
            </button>
          )}
        </div>
      </div>

      {/* Inventory Table Preview */}
      <DataTable
        className="inventory-container"
        title="Estado_Inventario_Crítico"
        subtitle={`MOSTRANDO ${criticalBooks.length} DE ${criticalTotal} REGISTROS`}
        data={criticalBooks}
        isLoading={criticalLoading}
        emptyText="Ningún libro con stock bajo"
        getRowId={(book) => book.id}
        columns={[
          { id: 'id', header: 'ID_REF', className: 'cell-id' },
          { id: 'title', header: 'Título_Obra', className: 'cell-title' },
          { id: 'author', header: 'Autor' },
          {
            id: 'status',
            header: 'Estado',
            render: (book) => (
              <span className={`status-badge ${book.stock <= 0 ? 'status-other' : 'status-warehouse'}`}>
                {book.stock <= 0 ? 'SIN STOCK' : 'STOCK BAJO'}
              </span>
            )
          },
          { id: 'stock', header: 'Stock' },
          { id: 'onOrder', header: 'En_Pedido' },
          {
            id: 'price',
            header: 'Precio',
            render: (book) => euros(book.price)
          }
        ]}
      />
    </div>
  );
};
